import type {
  MRT_ColumnFiltersState,
  MRT_RowSelectionState,
  MRT_SortingState,
} from 'mantine-react-table';
import React, {useState} from 'react';
import {useDidUpdate} from '@mantine/hooks';
import type {ComplianceStandardResponse} from 'src/api/ProactApiClient';
import {ModuleEnum} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {CheckboxRow} from 'src/design/molecules/CheckboxRow';
import {
  useBulkActivateInstanceComplianceStandardsMutation,
  useGetComplianceStandardListV2Query,
} from 'src/redux/queries/complianceStandard';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {mergeComplianceScope} from 'src/app/compliance-standards/complianceStandardTreeState';
import {useResetOnScopeChange} from 'src/app/tree-browser/useResetOnScopeChange';
import {useInstanceComplianceStandardColumns} from './hooks/useInstanceComplianceStandardColumns';

export interface InstanceComplianceStandardProps {
  identifier: string;
  defaultSorting?: MRT_SortingState;
  defaultColumnFilters?: MRT_ColumnFiltersState;
  onViewRowClick: (row: ComplianceStandardResponse) => void;
  scopeNodeId?: string;
  activeOnly: boolean;
  onActiveOnlyChange: (activeOnly: boolean) => void;
}

export const InstanceComplianceStandardTable: React.FC<
  InstanceComplianceStandardProps
> = ({
  identifier,
  defaultSorting,
  defaultColumnFilters,
  onViewRowClick,
  scopeNodeId,
  activeOnly,
  onActiveOnlyChange,
}) => {
  const {setAlert} = useAlert();
  const perms = useModulePermissions(ModuleEnum.Compliance);

  function handleActiveFilter(_: unknown, checked: boolean): void {
    onActiveOnlyChange(checked);
  }

  const columns = useInstanceComplianceStandardColumns();

  const tableState = useTableState({
    identifier: identifier,
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: defaultSorting ?? [{id: 'sectionLabel', desc: false}],
    defaultShowColumnFilters: true,
    defaultColumnFilters: defaultColumnFilters ?? [],
  });

  const {start, length, filters, filterFns, sorting} = tableState;
  const scopedRequest = mergeComplianceScope(filters, filterFns, scopeNodeId);

  const {currentData, isLoading, isError, isFetching, refetch} =
    useGetComplianceStandardListV2Query(
      {
        start: start,
        length: length,
        filters: scopedRequest.filters,
        filterFns: scopedRequest.filterFns,
        sorting: sorting,
        active: activeOnly,
      },
      {refetchOnMountOrArgChange: true}
    );

  const rows = currentData?.data ?? [];
  const totalCount = currentData?.totalRecords ?? 0;

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const resetPageAndSelection = () => {
    tableState.setPagination(current => ({...current, pageIndex: 0}));
    setRowSelection({});
  };
  useResetOnScopeChange(scopeNodeId, resetPageAndSelection);
  useDidUpdate(resetPageAndSelection, [activeOnly]);
  const selectedCount = Object.keys(rowSelection ?? {}).length;

  const [bulkActivate] = useBulkActivateInstanceComplianceStandardsMutation();

  const [isBulkLoading, setIsBulkLoading] = useState(false);
  const handleBulkActivateStandards = async () => {
    setIsBulkLoading(true);

    try {
      const ids = Object.keys(rowSelection).map(id => +id);
      await bulkActivate({activateIds: ids}).unwrap();

      setRowSelection({});
      void refetch();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsBulkLoading(false);
  };

  const {table} = useTable({
    ...tableState,
    rowSelection: rowSelection,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      enableRowSelection: perms.update,
      enableRowActions: true,
      columns: columns,
      data: rows,
      onRowSelectionChange: setRowSelection,
      getRowId: row => String(row.id),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View onClick={() => onViewRowClick(row.original)} />
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Filters>
            <CheckboxRow
              label="Active Standards Only"
              checked={activeOnly}
              onChange={handleActiveFilter}
            />
          </TableToolbar.Filters>
          <TableToolbar.Actions>
            {perms.update && (
              <TableToolbar.Action
                variant="primary"
                disabled={selectedCount === 0 || isBulkLoading}
                loading={isBulkLoading}
                onClick={handleBulkActivateStandards}>
                {`Apply ${selectedCount} Changes`}
              </TableToolbar.Action>
            )}
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return <>{table}</>;
};
